import type { components } from '@multaqa/api-client'
import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export const TREND_MONTHS = ['6', '12', '24'] as const
export type TrendMonths = (typeof TREND_MONTHS)[number]
export const DEFAULT_TREND_MONTHS: TrendMonths = '12'

/**
 * Coerces a route search value into a valid `months` selection.
 *
 * The generated query param is a STRING union (`'6' | '12' | '24'`), but a
 * route search value can also arrive as a JavaScript number, so both forms
 * are accepted here; anything else falls back to `DEFAULT_TREND_MONTHS`.
 */
export function trendMonthsFrom(value: unknown): TrendMonths {
  const candidate = typeof value === 'number' ? String(value) : value
  return candidate === '6' || candidate === '12' || candidate === '24'
    ? candidate
    : DEFAULT_TREND_MONTHS
}

export type SalesTrendPeriod = components['schemas']['SalesTrendPeriodResource']
export type SalesTrendWarehouse = components['schemas']['SalesTrendWarehouseResource']
type SalesTrendReportPayload = components['schemas']['SalesTrendReportResource']

export const salesTrendKeys = {
  report: (companyId: string | null, months: TrendMonths) =>
    ['sales', companyId, 'reports', 'trend', months] as const,
}

export function useSalesTrendReport(
  companyId: string | null,
  months: TrendMonths,
  options?: Omit<UseQueryOptions<SalesTrendReportPayload, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SalesTrendReportPayload, Error>({
    queryKey: salesTrendKeys.report(companyId, months),
    enabled: Boolean(companyId),
    staleTime: 30_000,
    ...options,
    queryFn: async () => {
      if (!companyId) throw new Error('An active company is required to load this report.')

      const response = await api.GET('/sales/reports/trend', {
        headers: { 'X-Company-Id': companyId },
        params: { query: { months } },
      })

      if (response.error) {
        throw new Error('The sales trend report request failed.')
      }
      if (!response.data) {
        throw new Error('The sales trend report returned no data.')
      }

      return response.data.data
    },
  })
}
